import { Palette, useTheme } from "@amx/ux-twoui-theming";
import { Alignment, IStackItemProps, IStackItemStyles, IStackItemTokens, IStackProps, IStackStyles, IStackTokens, Stack as FluentStack, StackItem as FluentStackItem } from "@fluentui/react";
import React, { ReactElement } from "react";
import { BaseProps, validateProps } from "../../../shared/props";
import { ComponentValidator, validateComponent } from "../../../shared/validation";
import { boxShadowDepths, gutterSize } from "../../../styles/constants";
import { TextAlignment } from "../../../styles/fonts";
import { SlotStackItemProps as BaseSlotStackItemProps, SlotStackProps as BaseSlotStackProps, withSlots } from "../SlotStack/SlotStack";

export type { Alignment };
export type GrowOptions = IStackProps["grow"];
export type StackItemAlign = IStackItemProps["align"];
export type BoxSizing = "content-box" | "border-box" | "inherit";
export type BorderRadiusVariant = "0px" | "4px" | "8px";

export type StackBackgroundColor = Extract<keyof Palette,
    "neutralHigh"
    | "neutralMed"
    | "transparent"
    | "neutralLighterAlt"
    | "white"
    | "neutralLighter"
    | "neutralLight"
>;

/**
 * Props shared by Stack and StackItem
 */
interface SharedStackProps extends BaseProps {
    /**
     * Children to render inside Stack
     */
    readonly children?: React.ReactNode;
    /**
     * How much to grow the Stack in proportion to its siblings.
     */
    readonly grow?: GrowOptions;
    /**
     * Height for the Stack/Item
     */
    readonly height?: number | string;
    /**
     * Width for the Stack/Item
     */
    readonly width?: number | string;
    /**
     * Margin to be applied to the Stack/Item relative to its container.
     */
    readonly margin?: number | string;
    /**
     * Padding to be applied to the Stack/Item contents relative to its border.
     */
    readonly padding?: number | string;
    /**
     * Alignment of the text within the stack
     */
    readonly textAlignment?: TextAlignment;
    /**
     * Background color of the stack
     */
    readonly backgroundColor?: StackBackgroundColor;
    /**
     * Whether or not to have a drop shadow
     */
    readonly boxShadow?: boolean;
    /**
     * Border Radius used in the component layout. Default is 0px.
     */
    readonly borderRadius?: BorderRadiusVariant;
}

/**
 * Props for Stack component
 */
export interface StackProps extends SharedStackProps {
    /** The type of stack */
    readonly type?: "stack";

    /**
     * Render Stack as another element type
     */
    readonly as?: React.ElementType<React.HTMLAttributes<HTMLElement>>;
    /**
     * Spacing between Stack children. The property is specified as a value for 'row gap', followed optionally
     * by a value for 'column gap'. If 'column gap' is omitted, it's set to the same value as 'row gap'.
     */
    readonly childrenGap?: number | string;
    /**
     * Whether to render Stack children horizontally.
     */
    readonly horizontal?: boolean;
    /**
     * How to align Stack children horizontally (along the x-axis).
     */
    readonly horizontalAlign?: Alignment;
    /**
     * Maximum height for the Stack.
     */
    readonly maxHeight?: number | string;
    /**
     * Maximum width for the Stack.
     */
    readonly maxWidth?: number | string;
    /**
     * Overflow behavior of the Stack.
     */
    readonly overflow?: string;
    /**
     * Tab index value of the stack.
     */
    readonly tabIndex?: number;
    /**
     * Whether to render Stack children in the opposite direction (bottom-to-top if it's a vertical Stack and
     * right-to-left if it's a horizontal Stack).
     */
    readonly reversed?: boolean;
    /**
     * How to align Stack children vertically (along the y-axis).
     */
    readonly verticalAlign?: Alignment;
    /**
     * Sets whether children should wrap into multiple rows or columns when they overflow the size of the Stack
     * Default is set to false
     */
    readonly wrap?: boolean;
    /**
     * Whether to render a border around Stack
     */
    readonly withBorder?: boolean;

    // SlotStack props
    readonly totalSlots?: undefined;
}

export interface SlotStackProps extends BaseSlotStackProps {
    readonly type: "slots";

    // SlotStack doesn't allow these props
    readonly childrenGap?: undefined;
    readonly horizontal?: undefined;
    readonly width?: undefined;
    readonly maxWidth?: undefined;
    readonly tabIndex?: undefined;
    readonly wrap?: undefined;
    readonly boxShadow?: boolean;
    readonly borderRadius?: BorderRadiusVariant;
}

/**
 * Props for StackItem component
 */
export interface StackItemProps extends SharedStackProps {
    /**
     * How to align the StackItem along the x-axis (for vertical Stacks) or the y-axis (for horizontal Stacks), relative to its container.
     */
    readonly align?: StackItemAlign;
    /**
     * At what ratio should the StackItem shrink to fit the available space.
     */
    readonly shrink?: GrowOptions;
    /**
     * What type of box-sizing to apply to this StackItem
     */
    readonly boxSizing?: BoxSizing;

    // SlotStackItem props
    readonly slots?: undefined;
}

export interface SlotStackItemProps extends BaseSlotStackItemProps {
    // SlotStackItem doesn't allow these props
    readonly margin?: undefined;
    readonly padding?: undefined;
    readonly width?: undefined;
    readonly boxSizing?: undefined;
}

const slotStackChildrenValidator = (child: React.ReactNode): ComponentValidator => (
    () => (!React.isValidElement<SlotStackItemProps>(child) || child.type !== StackItem || !child.props.slots ?
        `<SlotStack type="slots"> only accepts children of type <StackItem slot="x">.` : undefined)
);

/**
 * Stack Component
 */
export const Stack: React.FC<StackProps | SlotStackProps> = ({
    type,
    children,
    childrenGap,
    horizontal,
    height,
    width,
    maxHeight,
    maxWidth,
    overflow,
    tabIndex,
    margin,
    padding,
    backgroundColor,
    reversed = false,
    textAlignment,
    totalSlots,
    wrap = false,
    withBorder = false,
    boxShadow = false,
    borderRadius,
    ...otherProps
}) => {

    validateProps("Stack", otherProps);

    const theme = useTheme();
    const styles: Partial<IStackStyles> = { root: {} };
    const tokens: IStackTokens = {};

    tokens.childrenGap = type === "slots" ? gutterSize : childrenGap;

    if (maxHeight) {
        tokens.maxHeight = maxHeight;
    }
    if (maxWidth) {
        tokens.maxWidth = maxWidth;
    }
    if (overflow) {
        styles.root!["overflow"] = overflow;
    }
    if (margin) {
        styles.root!["margin"] = margin;
    }
    if (padding) {
        styles.root!["padding"] = padding;
    }
    if (height) {
        styles.root!["height"] = height;
    }
    if (width) {
        styles.root!["width"] = width;
    }
    if (withBorder) {
        styles.root!["borderWidth"] = "1px";
        styles.root!["borderStyle"] = "solid";
        styles.root!["borderColor"] = theme.palette.neutralSecondary;
        styles.root!["borderRadius"] = "2px";
    }
    if (textAlignment) {
        styles.root!["text-align"] = textAlignment;
    }
    if (backgroundColor) {
        styles.root!["background-color"] = theme.palette[backgroundColor];
    }
    if (boxShadow) {
        styles.root!["boxShadow"] = boxShadowDepths.rest;
    }
    if (borderRadius) {
        styles.root!["border-radius"] = borderRadius;
    }

    if (type === "slots") {
        // Don't use React.Children here because it modifies the children's keys; it's important that the keys
        // stay unchanged for proper reconciliation between views.
        const childArray = Array.isArray(children) ? children : [children];
        const mappedChildren = withSlots(childArray.map(child => {
            validateComponent("Stack", [slotStackChildrenValidator(child)]);

            return (child as ReactElement<SlotStackItemProps>);
        }), totalSlots!);

        return (
            <FluentStack
                horizontal={true}
                reversed={reversed}
                styles={styles}
                tokens={tokens}
                wrap={false}
                {...otherProps}
            >
                {mappedChildren}
            </FluentStack>
        );
    }

    return (
        <FluentStack
            horizontal={horizontal}
            reversed={reversed}
            styles={styles}
            tokens={tokens}
            wrap={wrap}
            tabIndex={tabIndex}
            {...otherProps}
        >
            {children}
        </FluentStack>
    );
};

/**
 * StackItem component
 */
export const StackItem: React.FC<StackItemProps | SlotStackItemProps> = ({
    children = <></>, //ensures that the stack always renders - see https://microsoft.visualstudio.com/OSGS/_workitems/edit/28393762/
    margin,
    height,
    padding,
    width,
    boxSizing,
    textAlignment,
    ...otherProps
}) => {

    validateProps("StackItem", otherProps);

    const tokens: IStackItemTokens = {};
    const styles: Partial<IStackItemStyles> = { root: {} };
    if (margin) {
        styles.root!["margin"] = margin;
    }
    if (padding) {
        styles.root!["padding"] = padding;
    }
    if (height) {
        styles.root!["height"] = height;
    }
    if (width) {
        styles.root!["width"] = width;
    }
    if (boxSizing) {
        styles.root!["boxSizing"] = boxSizing;
    }
    if (textAlignment) {
        styles.root!["text-align"] = textAlignment;
    }

    return (
        <FluentStackItem
            styles={styles}
            tokens={tokens}
            {...otherProps}
        >
            {children}
        </FluentStackItem>
    );
};
